/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { AwbMode_Enum, awbMode_EnumFromJSON, awbMode_EnumToJSON } from "../../gen/user_camera/awb_mode";
import {
  CompensationMode_Enum,
  compensationMode_EnumFromJSON,
  compensationMode_EnumToJSON,
} from "../../gen/user_camera/compensation_mode";
import {
  ExposureMode_Enum,
  exposureMode_EnumFromJSON,
  exposureMode_EnumToJSON,
} from "../../gen/user_camera/exposure_mode";
import { IsoMode_Enum, isoMode_EnumFromJSON, isoMode_EnumToJSON } from "../../gen/user_camera/iso_mode";
import {
  RecordingMode_Enum,
  recordingMode_EnumFromJSON,
  recordingMode_EnumToJSON,
} from "../../gen/user_camera/recording_mode";

export const protobufPackage = "skills";

/**
 * General settings for camera operations. Includes settings for iso mode, exposure mode, auto white
 * balance mode, zoom level, EV compensation level, and recording mode.
 */
export interface CameraSettings {
  /** Desired iso mode */
  isoMode: IsoMode_Enum;
  /** Desired exposure mode (shutter speed) */
  exposureMode: ExposureMode_Enum;
  /** Desired auto white balance mode */
  awbMode: AwbMode_Enum;
  /** Desired zoom level */
  zoomLevel: number;
  eoSensor: CameraSettings_EoSensor;
  /** EV compensation level */
  compensationMode: CompensationMode_Enum;
  /** Desired recording mode */
  recordingMode: RecordingMode_Enum;
}

/** Desired EO sensor. It may not be compatible with the zoom_level. @exclude */
export enum CameraSettings_EoSensor {
  UNKNOWN = 0,
  R3_WIDE = 1,
  X2_WIDE = 2,
  X2_NARROW = 3,
  R47_NARROW = 5,
  R47_TELE = 6,
  R47_CINE = 7,
  UNRECOGNIZED = -1,
}

export function cameraSettings_EoSensorFromJSON(object: any): CameraSettings_EoSensor {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return CameraSettings_EoSensor.UNKNOWN;
    case 1:
    case "R3_WIDE":
      return CameraSettings_EoSensor.R3_WIDE;
    case 2:
    case "X2_WIDE":
      return CameraSettings_EoSensor.X2_WIDE;
    case 3:
    case "X2_NARROW":
      return CameraSettings_EoSensor.X2_NARROW;
    case 5:
    case "R47_NARROW":
      return CameraSettings_EoSensor.R47_NARROW;
    case 6:
    case "R47_TELE":
      return CameraSettings_EoSensor.R47_TELE;
    case 7:
    case "R47_CINE":
      return CameraSettings_EoSensor.R47_CINE;
    case -1:
    case "UNRECOGNIZED":
    default:
      return CameraSettings_EoSensor.UNRECOGNIZED;
  }
}

export function cameraSettings_EoSensorToJSON(object: CameraSettings_EoSensor): string {
  switch (object) {
    case CameraSettings_EoSensor.UNKNOWN:
      return "UNKNOWN";
    case CameraSettings_EoSensor.R3_WIDE:
      return "R3_WIDE";
    case CameraSettings_EoSensor.X2_WIDE:
      return "X2_WIDE";
    case CameraSettings_EoSensor.X2_NARROW:
      return "X2_NARROW";
    case CameraSettings_EoSensor.R47_NARROW:
      return "R47_NARROW";
    case CameraSettings_EoSensor.R47_TELE:
      return "R47_TELE";
    case CameraSettings_EoSensor.R47_CINE:
      return "R47_CINE";
    case CameraSettings_EoSensor.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Settings for capturing photos. */
export interface PhotoSettings {
  /** Type of photo to capture (visible light and/or infrared). */
  photoType:
    | PhotoType
    | undefined;
  /** Format for storing photos. */
  photoFormat:
    | PhotoFormat
    | undefined;
  /** Quality of the photo from 0 to 100 */
  photoQuality: number;
  /** Thumbnail settings (0 width means default width used by the capture stack) */
  publishThumbnail: boolean;
  thumbnailWidth: number;
  /** [Alpha] Formats to be captured for different sensing modalities. */
  photoFormatSettings: PhotoFormatSettings | undefined;
}

/** Type of photo to capture (visible light and/or infrared). */
export interface PhotoType {
  /**
   * Indicates whether to take a visible light photo
   * If not defined, it WILL take an eo photo
   */
  eoPhoto: boolean;
  /**
   * Indicates whether to take an infrared photo.
   * If not defined, it WILL NOT take an ir photo
   */
  irPhoto: boolean;
}

export interface PhotoFormat {
  /**
   * Optional: The desired photo format: 'JPEG', 'DNG', 'RAW', 'BOTH' or None
   * If not defined, None
   */
  format: string;
}

export interface PhotoFormatSettings {
  /** Photo formats for EO. Empty means no EO photos. */
  eoPhotoFormats: PhotoFormatSettings_PhotoFormat[];
  /** Photo formats for IR. Empty means no IR photos. */
  irPhotoFormats: PhotoFormatSettings_PhotoFormat[];
}

/**
 * NOTE(Ayush): not using `user_camera.PhotoFormat` to prevent mission spec dependency @exclude
 * Format of the photo to be captured.
 */
export enum PhotoFormatSettings_PhotoFormat {
  JPEG = 0,
  DNG = 1,
  RAW = 2,
  RJPEG = 3,
  UNRECOGNIZED = -1,
}

export function photoFormatSettings_PhotoFormatFromJSON(object: any): PhotoFormatSettings_PhotoFormat {
  switch (object) {
    case 0:
    case "JPEG":
      return PhotoFormatSettings_PhotoFormat.JPEG;
    case 1:
    case "DNG":
      return PhotoFormatSettings_PhotoFormat.DNG;
    case 2:
    case "RAW":
      return PhotoFormatSettings_PhotoFormat.RAW;
    case 3:
    case "RJPEG":
      return PhotoFormatSettings_PhotoFormat.RJPEG;
    case -1:
    case "UNRECOGNIZED":
    default:
      return PhotoFormatSettings_PhotoFormat.UNRECOGNIZED;
  }
}

export function photoFormatSettings_PhotoFormatToJSON(object: PhotoFormatSettings_PhotoFormat): string {
  switch (object) {
    case PhotoFormatSettings_PhotoFormat.JPEG:
      return "JPEG";
    case PhotoFormatSettings_PhotoFormat.DNG:
      return "DNG";
    case PhotoFormatSettings_PhotoFormat.RAW:
      return "RAW";
    case PhotoFormatSettings_PhotoFormat.RJPEG:
      return "RJPEG";
    case PhotoFormatSettings_PhotoFormat.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

function createBaseCameraSettings(): CameraSettings {
  return { isoMode: 0, exposureMode: 0, awbMode: 0, zoomLevel: 0, eoSensor: 0, compensationMode: 0, recordingMode: 0 };
}

export const CameraSettings = {
  encode(message: CameraSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.isoMode !== 0) {
      writer.uint32(8).int32(message.isoMode);
    }
    if (message.exposureMode !== 0) {
      writer.uint32(16).int32(message.exposureMode);
    }
    if (message.awbMode !== 0) {
      writer.uint32(24).int32(message.awbMode);
    }
    if (message.zoomLevel !== 0) {
      writer.uint32(37).float(message.zoomLevel);
    }
    if (message.eoSensor !== 0) {
      writer.uint32(56).int32(message.eoSensor);
    }
    if (message.compensationMode !== 0) {
      writer.uint32(40).int32(message.compensationMode);
    }
    if (message.recordingMode !== 0) {
      writer.uint32(48).int32(message.recordingMode);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): CameraSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCameraSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.isoMode = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.exposureMode = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.awbMode = reader.int32() as any;
          continue;
        case 4:
          if (tag !== 37) {
            break;
          }

          message.zoomLevel = reader.float();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.eoSensor = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.compensationMode = reader.int32() as any;
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.recordingMode = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): CameraSettings {
    return {
      isoMode: isSet(object.isoMode) ? isoMode_EnumFromJSON(object.isoMode) : 0,
      exposureMode: isSet(object.exposureMode) ? exposureMode_EnumFromJSON(object.exposureMode) : 0,
      awbMode: isSet(object.awbMode) ? awbMode_EnumFromJSON(object.awbMode) : 0,
      zoomLevel: isSet(object.zoomLevel) ? globalThis.Number(object.zoomLevel) : 0,
      eoSensor: isSet(object.eoSensor) ? cameraSettings_EoSensorFromJSON(object.eoSensor) : 0,
      compensationMode: isSet(object.compensationMode) ? compensationMode_EnumFromJSON(object.compensationMode) : 0,
      recordingMode: isSet(object.recordingMode) ? recordingMode_EnumFromJSON(object.recordingMode) : 0,
    };
  },

  toJSON(message: CameraSettings): unknown {
    const obj: any = {};
    if (message.isoMode !== 0) {
      obj.isoMode = isoMode_EnumToJSON(message.isoMode);
    }
    if (message.exposureMode !== 0) {
      obj.exposureMode = exposureMode_EnumToJSON(message.exposureMode);
    }
    if (message.awbMode !== 0) {
      obj.awbMode = awbMode_EnumToJSON(message.awbMode);
    }
    if (message.zoomLevel !== 0) {
      obj.zoomLevel = message.zoomLevel;
    }
    if (message.eoSensor !== 0) {
      obj.eoSensor = cameraSettings_EoSensorToJSON(message.eoSensor);
    }
    if (message.compensationMode !== 0) {
      obj.compensationMode = compensationMode_EnumToJSON(message.compensationMode);
    }
    if (message.recordingMode !== 0) {
      obj.recordingMode = recordingMode_EnumToJSON(message.recordingMode);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<CameraSettings>, I>>(base?: I): CameraSettings {
    return CameraSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<CameraSettings>, I>>(object: I): CameraSettings {
    const message = createBaseCameraSettings();
    message.isoMode = object.isoMode ?? 0;
    message.exposureMode = object.exposureMode ?? 0;
    message.awbMode = object.awbMode ?? 0;
    message.zoomLevel = object.zoomLevel ?? 0;
    message.eoSensor = object.eoSensor ?? 0;
    message.compensationMode = object.compensationMode ?? 0;
    message.recordingMode = object.recordingMode ?? 0;
    return message;
  },
};

function createBasePhotoSettings(): PhotoSettings {
  return {
    photoType: undefined,
    photoFormat: undefined,
    photoQuality: 0,
    publishThumbnail: false,
    thumbnailWidth: 0,
    photoFormatSettings: undefined,
  };
}

export const PhotoSettings = {
  encode(message: PhotoSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.photoType !== undefined) {
      PhotoType.encode(message.photoType, writer.uint32(50).fork()).ldelim();
    }
    if (message.photoFormat !== undefined) {
      PhotoFormat.encode(message.photoFormat, writer.uint32(58).fork()).ldelim();
    }
    if (message.photoQuality !== 0) {
      writer.uint32(64).int64(message.photoQuality);
    }
    if (message.publishThumbnail === true) {
      writer.uint32(72).bool(message.publishThumbnail);
    }
    if (message.thumbnailWidth !== 0) {
      writer.uint32(80).int32(message.thumbnailWidth);
    }
    if (message.photoFormatSettings !== undefined) {
      PhotoFormatSettings.encode(message.photoFormatSettings, writer.uint32(90).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PhotoSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePhotoSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 6:
          if (tag !== 50) {
            break;
          }

          message.photoType = PhotoType.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.photoFormat = PhotoFormat.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.photoQuality = longToNumber(reader.int64() as Long);
          continue;
        case 9:
          if (tag !== 72) {
            break;
          }

          message.publishThumbnail = reader.bool();
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.thumbnailWidth = reader.int32();
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.photoFormatSettings = PhotoFormatSettings.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PhotoSettings {
    return {
      photoType: isSet(object.photoType) ? PhotoType.fromJSON(object.photoType) : undefined,
      photoFormat: isSet(object.photoFormat) ? PhotoFormat.fromJSON(object.photoFormat) : undefined,
      photoQuality: isSet(object.photoQuality) ? globalThis.Number(object.photoQuality) : 0,
      publishThumbnail: isSet(object.publishThumbnail) ? globalThis.Boolean(object.publishThumbnail) : false,
      thumbnailWidth: isSet(object.thumbnailWidth) ? globalThis.Number(object.thumbnailWidth) : 0,
      photoFormatSettings: isSet(object.photoFormatSettings)
        ? PhotoFormatSettings.fromJSON(object.photoFormatSettings)
        : undefined,
    };
  },

  toJSON(message: PhotoSettings): unknown {
    const obj: any = {};
    if (message.photoType !== undefined) {
      obj.photoType = PhotoType.toJSON(message.photoType);
    }
    if (message.photoFormat !== undefined) {
      obj.photoFormat = PhotoFormat.toJSON(message.photoFormat);
    }
    if (message.photoQuality !== 0) {
      obj.photoQuality = Math.round(message.photoQuality);
    }
    if (message.publishThumbnail === true) {
      obj.publishThumbnail = message.publishThumbnail;
    }
    if (message.thumbnailWidth !== 0) {
      obj.thumbnailWidth = Math.round(message.thumbnailWidth);
    }
    if (message.photoFormatSettings !== undefined) {
      obj.photoFormatSettings = PhotoFormatSettings.toJSON(message.photoFormatSettings);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PhotoSettings>, I>>(base?: I): PhotoSettings {
    return PhotoSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PhotoSettings>, I>>(object: I): PhotoSettings {
    const message = createBasePhotoSettings();
    message.photoType = (object.photoType !== undefined && object.photoType !== null)
      ? PhotoType.fromPartial(object.photoType)
      : undefined;
    message.photoFormat = (object.photoFormat !== undefined && object.photoFormat !== null)
      ? PhotoFormat.fromPartial(object.photoFormat)
      : undefined;
    message.photoQuality = object.photoQuality ?? 0;
    message.publishThumbnail = object.publishThumbnail ?? false;
    message.thumbnailWidth = object.thumbnailWidth ?? 0;
    message.photoFormatSettings = (object.photoFormatSettings !== undefined && object.photoFormatSettings !== null)
      ? PhotoFormatSettings.fromPartial(object.photoFormatSettings)
      : undefined;
    return message;
  },
};

function createBasePhotoType(): PhotoType {
  return { eoPhoto: false, irPhoto: false };
}

export const PhotoType = {
  encode(message: PhotoType, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.eoPhoto === true) {
      writer.uint32(8).bool(message.eoPhoto);
    }
    if (message.irPhoto === true) {
      writer.uint32(16).bool(message.irPhoto);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PhotoType {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePhotoType();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.eoPhoto = reader.bool();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.irPhoto = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PhotoType {
    return {
      eoPhoto: isSet(object.eoPhoto) ? globalThis.Boolean(object.eoPhoto) : false,
      irPhoto: isSet(object.irPhoto) ? globalThis.Boolean(object.irPhoto) : false,
    };
  },

  toJSON(message: PhotoType): unknown {
    const obj: any = {};
    if (message.eoPhoto === true) {
      obj.eoPhoto = message.eoPhoto;
    }
    if (message.irPhoto === true) {
      obj.irPhoto = message.irPhoto;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PhotoType>, I>>(base?: I): PhotoType {
    return PhotoType.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PhotoType>, I>>(object: I): PhotoType {
    const message = createBasePhotoType();
    message.eoPhoto = object.eoPhoto ?? false;
    message.irPhoto = object.irPhoto ?? false;
    return message;
  },
};

function createBasePhotoFormat(): PhotoFormat {
  return { format: "" };
}

export const PhotoFormat = {
  encode(message: PhotoFormat, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.format !== "") {
      writer.uint32(10).string(message.format);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PhotoFormat {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePhotoFormat();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.format = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PhotoFormat {
    return { format: isSet(object.format) ? globalThis.String(object.format) : "" };
  },

  toJSON(message: PhotoFormat): unknown {
    const obj: any = {};
    if (message.format !== "") {
      obj.format = message.format;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PhotoFormat>, I>>(base?: I): PhotoFormat {
    return PhotoFormat.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PhotoFormat>, I>>(object: I): PhotoFormat {
    const message = createBasePhotoFormat();
    message.format = object.format ?? "";
    return message;
  },
};

function createBasePhotoFormatSettings(): PhotoFormatSettings {
  return { eoPhotoFormats: [], irPhotoFormats: [] };
}

export const PhotoFormatSettings = {
  encode(message: PhotoFormatSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    writer.uint32(10).fork();
    for (const v of message.eoPhotoFormats) {
      writer.int32(v);
    }
    writer.ldelim();
    writer.uint32(18).fork();
    for (const v of message.irPhotoFormats) {
      writer.int32(v);
    }
    writer.ldelim();
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PhotoFormatSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePhotoFormatSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag === 8) {
            message.eoPhotoFormats.push(reader.int32() as any);

            continue;
          }

          if (tag === 10) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.eoPhotoFormats.push(reader.int32() as any);
            }

            continue;
          }

          break;
        case 2:
          if (tag === 16) {
            message.irPhotoFormats.push(reader.int32() as any);

            continue;
          }

          if (tag === 18) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.irPhotoFormats.push(reader.int32() as any);
            }

            continue;
          }

          break;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PhotoFormatSettings {
    return {
      eoPhotoFormats: globalThis.Array.isArray(object?.eoPhotoFormats)
        ? object.eoPhotoFormats.map((e: any) => photoFormatSettings_PhotoFormatFromJSON(e))
        : [],
      irPhotoFormats: globalThis.Array.isArray(object?.irPhotoFormats)
        ? object.irPhotoFormats.map((e: any) => photoFormatSettings_PhotoFormatFromJSON(e))
        : [],
    };
  },

  toJSON(message: PhotoFormatSettings): unknown {
    const obj: any = {};
    if (message.eoPhotoFormats?.length) {
      obj.eoPhotoFormats = message.eoPhotoFormats.map((e) => photoFormatSettings_PhotoFormatToJSON(e));
    }
    if (message.irPhotoFormats?.length) {
      obj.irPhotoFormats = message.irPhotoFormats.map((e) => photoFormatSettings_PhotoFormatToJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PhotoFormatSettings>, I>>(base?: I): PhotoFormatSettings {
    return PhotoFormatSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PhotoFormatSettings>, I>>(object: I): PhotoFormatSettings {
    const message = createBasePhotoFormatSettings();
    message.eoPhotoFormats = object.eoPhotoFormats?.map((e) => e) || [];
    message.irPhotoFormats = object.irPhotoFormats?.map((e) => e) || [];
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
